import React, { useState, useEffect } from 'react';
import { Modal, Switch, Typography, message, Space, Divider } from 'antd';
import { MailOutlined, SettingOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Text, Title } = Typography;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }

  .ant-modal-header {
    border-radius: 16px 16px 0 0;
    background: linear-gradient(135deg, #2DBCAF 0%, #18938a 100%);
    border: none;
    padding: 20px 24px;
  }

  .ant-modal-title {
    color: white;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ant-modal-body {
    padding: 24px;
  }
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const PreferenceContent = styled.div`
  flex: 1;
  margin-right: 16px;
`;

const PreferenceTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1f2e;
  margin-bottom: 4px;
`;

const PreferenceDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

interface EmailPreferences {
  marketing: boolean;
  weekly: boolean;
  tips: boolean;
  updates: boolean;
}

interface EmailPreferencesModalProps {
  visible: boolean;
  onClose: () => void;
}

const EmailPreferencesModal: React.FC<EmailPreferencesModalProps> = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    marketing: true,
    weekly: true,
    tips: true,
    updates: true,
  });

  useEffect(() => {
    if (visible) {
      fetchPreferences();
    }
  }, [visible]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/email/preferences');
      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      } else {
        message.error('Gagal memuat pengaturan email');
      }
    } catch (error) {
      console.error('Fetch preferences error:', error);
      message.error('Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (key: keyof EmailPreferences, value: boolean) => {
    setSaving(true);
    try {
      const newPreferences = { ...preferences, [key]: value };
      
      const response = await fetch('/api/email/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPreferences),
      });

      if (response.ok) {
        setPreferences(newPreferences);
        message.success('Pengaturan email berhasil diubah');
      } else {
        message.error('Gagal mengubah pengaturan');
      }
    } catch (error) {
      console.error('Update preferences error:', error);
      message.error('Terjadi kesalahan');
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchChange = (key: keyof EmailPreferences) => (checked: boolean) => {
    updatePreferences(key, checked);
  };

  return (
    <StyledModal
      title={
        <Space>
          <MailOutlined />
          Pengaturan Email
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ fontSize: '13px' }}>
          Atur jenis email yang ingin Anda terima dari KinderGame.id
        </Text>
      </div>

      <div>
        <PreferenceItem>
          <PreferenceContent>
            <PreferenceTitle>📧 Email Marketing</PreferenceTitle>
            <PreferenceDescription>
              Promo, diskon, dan penawaran khusus
            </PreferenceDescription>
          </PreferenceContent>
          <Switch
            checked={preferences.marketing}
            onChange={handleSwitchChange('marketing')}
            loading={saving}
            size="default"
          />
        </PreferenceItem>

        <PreferenceItem>
          <PreferenceContent>
            <PreferenceTitle>📬 Ringkasan Mingguan</PreferenceTitle>
            <PreferenceDescription>
              Game baru, tips parenting, dan update terbaru setiap minggu
            </PreferenceDescription>
          </PreferenceContent>
          <Switch
            checked={preferences.weekly}
            onChange={handleSwitchChange('weekly')}
            loading={saving}
            size="default"
          />
        </PreferenceItem>

        <PreferenceItem>
          <PreferenceContent>
            <PreferenceTitle>💡 Tips Parenting</PreferenceTitle>
            <PreferenceDescription>
              Artikel dan tips untuk tumbuh kembang anak
            </PreferenceDescription>
          </PreferenceContent>
          <Switch
            checked={preferences.tips}
            onChange={handleSwitchChange('tips')}
            loading={saving}
            size="default"
          />
        </PreferenceItem>

        <PreferenceItem>
          <PreferenceContent>
            <PreferenceTitle>🆕 Update Game Baru</PreferenceTitle>
            <PreferenceDescription>
              Notifikasi ketika ada game atau fitur baru
            </PreferenceDescription>
          </PreferenceContent>
          <Switch
            checked={preferences.updates}
            onChange={handleSwitchChange('updates')}
            loading={saving}
            size="default"
          />
        </PreferenceItem>
      </div>

      <Divider />

      <div style={{ textAlign: 'center' }}>
        <Text type="secondary" style={{ fontSize: '11px' }}>
          Anda masih akan menerima email penting seperti konfirmasi pembayaran
        </Text>
      </div>
    </StyledModal>
  );
};

// Komponen hook untuk unsubscribe page
interface UnsubscribePageProps {
  token?: string;
}

export const UnsubscribePage: React.FC<UnsubscribePageProps> = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (token) {
      handleUnsubscribe();
    }
  }, [token]);

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, type: 'marketing' }),
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Gagal berhenti berlangganan');
      }
    } catch (err) {
      setError('Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  const UnsubscribeWrapper = styled.div`
    max-width: 480px;
    margin: 80px auto;
    padding: 40px 24px;
    text-align: center;
  `;

  const UnsubscribeCard = styled.div`
    background: white;
    border-radius: 16px;
    padding: 48px 32px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  `;

  if (loading) {
    return (
      <UnsubscribeWrapper>
        <UnsubscribeCard>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <Title level={3}>Memproses...</Title>
          <Text type="secondary">Sedang membatalkan langganan email Anda</Text>
        </UnsubscribeCard>
      </UnsubscribeWrapper>
    );
  }

  if (success) {
    return (
      <UnsubscribeWrapper>
        <UnsubscribeCard>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <Title level={3}>Berhasil Berhenti Berlangganan</Title>
          <Text type="secondary">
            Anda tidak akan lagi menerima email marketing dari KinderGame.id.
            <br />
            Email penting seperti konfirmasi pembayaran tetap akan dikirim.
          </Text>
          <div style={{ marginTop: '24px' }}>
            <a href="/" style={{ color: '#2DBCAF', fontWeight: 600 }}>
              ← Kembali ke KinderGame.id
            </a>
          </div>
        </UnsubscribeCard>
      </UnsubscribeWrapper>
    );
  }

  if (error) {
    return (
      <UnsubscribeWrapper>
        <UnsubscribeCard>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
          <Title level={3}>Gagal Berhenti Berlangganan</Title>
          <Text type="secondary">{error}</Text>
          <div style={{ marginTop: '24px' }}>
            <a href="/" style={{ color: '#2DBCAF', fontWeight: 600 }}>
              ← Kembali ke KinderGame.id
            </a>
          </div>
        </UnsubscribeCard>
      </UnsubscribeWrapper>
    );
  }

  return null;
};

export default EmailPreferencesModal;